// src/components/pacientes/ModalImportarPacientes.js
// =========================================================================
// IMPORTAR PACIENTES DESDE OTRO PROGRAMA (Excel o CSV)
//
//   1. Elegir el archivo (o descargar la plantilla).
//   2. Revisar qué columna del archivo va a cada campo de la ficha (se
//      reconocen solas las habituales: «Nombre», «NIF», «Móvil», «Población»…).
//   3. Comprobar: el servidor revisa cada fila con las mismas reglas que el
//      alta normal y dice cuáles entrarían, cuáles ya existen y qué falla.
//   4. Importar las válidas.
//
// El archivo se lee en el navegador; al servidor solo llegan las filas, en
// tandas, y nada se guarda hasta el paso 4.
// =========================================================================
import React, { useMemo, useState } from 'react';
import Papa from 'papaparse';
import { readSheet } from 'read-excel-file/browser';
import api from '../../services/api';
import { Modal } from '../common/ui';

const TANDA = 100;
const MAX_FILAS = 5000;

// Campo de la ficha → nombres de columna con los que suele venir
const CAMPOS = [
    { id: 'nombre', texto: 'Nombre', obligatorio: true, alias: ['nombre', 'name', 'nombrepaciente'] },
    { id: 'apellidos', texto: 'Apellidos', obligatorio: true, alias: ['apellidos', 'apellido', 'surname', 'apellido1'] },
    { id: 'dni', texto: 'DNI / NIE', obligatorio: true, alias: ['dni', 'nif', 'nie', 'dninie', 'documento', 'nifnie'] },
    { id: 'fechaNacimiento', texto: 'Fecha de nacimiento', obligatorio: true, alias: ['fechanacimiento', 'fechadenacimiento', 'nacimiento', 'fnacimiento', 'fechanac'] },
    { id: 'telefono', texto: 'Teléfono', obligatorio: true, alias: ['telefono', 'movil', 'tel', 'telefonomovil', 'celular', 'telefono1'] },
    { id: 'direccion', texto: 'Dirección', obligatorio: true, alias: ['direccion', 'domicilio', 'calle', 'address'] },
    { id: 'email', texto: 'Correo', alias: ['email', 'correo', 'correoelectronico', 'mail', 'email1'] },
    { id: 'ciudad', texto: 'Ciudad', alias: ['ciudad', 'poblacion', 'localidad', 'municipio'] },
    { id: 'codigoPostal', texto: 'Código postal', alias: ['codigopostal', 'cp', 'cpostal'] },
    { id: 'genero', texto: 'Género', alias: ['genero', 'sexo'] },
    { id: 'grupoSanguineo', texto: 'Grupo sanguíneo', alias: ['gruposanguineo', 'grupo', 'gruposang'] },
    { id: 'alergias', texto: 'Alergias', alias: ['alergias', 'alergia'] },
    { id: 'antecedentes', texto: 'Antecedentes', alias: ['antecedentes', 'enfermedades', 'patologias', 'antecedentespersonales'] },
    { id: 'medicacion', texto: 'Medicación', alias: ['medicacion', 'medicamentos', 'tratamientohabitual'] }
];

const clave = (t) => String(t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Valor de una celda a texto (las fechas de Excel llegan como Date)
const aTexto = (v) => {
    if (v === null || v === undefined) return '';
    // Excel no guarda zona horaria: la fecha llega como medianoche UTC
    if (v instanceof Date) return v.toISOString().slice(0, 10);
    return String(v).trim();
};

function descargarPlantilla() {
    const cabecera = CAMPOS.map(c => c.texto).join(';');
    const ejemplo = ['Ana', 'García López', '12345678Z', '14/03/1985', '600112233', 'C/ Mayor 10, 2º A', 'ana@ejemplo.es', 'Málaga', '29001', 'Mujer', 'A+', 'Penicilina', '', ''].join(';');
    const blob = new Blob([`\uFEFF${cabecera}\n${ejemplo}\n`], { type: 'text/csv;charset=utf-8' });
    const enlace = document.createElement('a');
    enlace.href = URL.createObjectURL(blob);
    enlace.download = 'plantilla-pacientes-klyndu.csv';
    enlace.click();
    URL.revokeObjectURL(enlace.href);
}

async function leerArchivo(archivo) {
    if (/\.xlsx$/i.test(archivo.name)) {
        const filas = await readSheet(archivo);
        return filas.map(f => f.map(aTexto));
    }
    if (/\.(csv|txt)$/i.test(archivo.name)) {
        const resultado = await new Promise((ok, mal) => Papa.parse(archivo, { skipEmptyLines: 'greedy', complete: ok, error: mal }));
        return resultado.data.map(f => f.map(aTexto));
    }
    throw new Error('Formato no admitido. Usa un archivo .xlsx o .csv (en Excel: Guardar como → CSV).');
}

const estiloSelect = 'w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-semibold text-slate-800 focus:outline-none focus:border-clinica-primary';

export default function ModalImportarPacientes({ onClose, onTerminado }) {
    const [archivo, setArchivo] = useState(null);
    const [cabecera, setCabecera] = useState([]);
    const [filas, setFilas] = useState([]);
    const [mapa, setMapa] = useState({}); // campo → índice de columna ('' = sin columna)
    const [paso, setPaso] = useState('archivo'); // archivo | columnas | informe | hecho
    const [informe, setInforme] = useState(null);
    const [progreso, setProgreso] = useState(null); // { hechas, total }
    const [error, setError] = useState('');

    const elegir = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f) return;
        setError('');
        try {
            const todas = (await leerArchivo(f)).filter(r => r.some(c => c !== ''));
            if (todas.length < 2) throw new Error('El archivo no tiene filas de pacientes (la primera fila debe ser la de los títulos de las columnas).');
            if (todas.length - 1 > MAX_FILAS) throw new Error(`El archivo tiene ${todas.length - 1} filas; el máximo por importación es ${MAX_FILAS}. Divídelo en varios.`);
            const [titulos, ...resto] = todas;
            const claves = titulos.map(clave);
            const automatico = {};
            for (const campo of CAMPOS) {
                const i = claves.findIndex(c => campo.alias.includes(c));
                automatico[campo.id] = i >= 0 ? String(i) : '';
            }
            setArchivo(f);
            setCabecera(titulos);
            setFilas(resto);
            setMapa(automatico);
            setPaso('columnas');
        } catch (err) {
            setError(err.message || 'No se ha podido leer el archivo.');
        }
    };

    const faltan = CAMPOS.filter(c => c.obligatorio && mapa[c.id] === '');
    const filasComoFicha = useMemo(() => filas.map(f => Object.fromEntries(
        CAMPOS.filter(c => mapa[c.id] !== '' && mapa[c.id] !== undefined).map(c => [c.id, f[Number(mapa[c.id])] ?? ''])
    )), [filas, mapa]);

    // Manda las filas en tandas y junta los informes
    const enviar = async (comprobar) => {
        setError('');
        const total = { total: 0, validas: 0, creadas: 0, existentes: [], errores: [] };
        setProgreso({ hechas: 0, total: filasComoFicha.length });
        try {
            for (let i = 0; i < filasComoFicha.length; i += TANDA) {
                const r = await api.post('/pacientes/importar', {
                    filas: filasComoFicha.slice(i, i + TANDA), comprobar, primeraFila: i + 2
                });
                total.total += r.total;
                total.validas += r.validas;
                total.creadas += r.creadas;
                total.existentes.push(...r.existentes);
                total.errores.push(...r.errores);
                setProgreso({ hechas: Math.min(i + TANDA, filasComoFicha.length), total: filasComoFicha.length });
            }
            setInforme(total);
            setPaso(comprobar ? 'informe' : 'hecho');
            if (!comprobar) onTerminado?.();
        } catch (err) {
            setError(err.message || 'No se ha podido completar. Lo ya importado se queda guardado; vuelve a comprobar para seguir.');
        } finally {
            setProgreso(null);
        }
    };

    const trabajando = Boolean(progreso);

    return (
        <Modal open onClose={trabajando ? () => {} : onClose} titulo="Importar pacientes" subtitulo="Desde Excel o CSV, por ejemplo exportado de tu programa anterior" size="xl">
            <div className="space-y-6">
                {error && <p role="alert" className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-sm font-semibold text-rose-700">{error}</p>}

                {paso === 'archivo' && (
                    <div className="space-y-5">
                        <label className="flex flex-col items-center justify-center gap-2 p-10 border-2 border-dashed border-slate-300 rounded-2xl cursor-pointer hover:border-clinica-primary hover:bg-slate-50 transition-colors">
                            <span className="text-sm font-black text-slate-800">Elegir archivo .xlsx o .csv</span>
                            <span className="text-xs font-semibold text-slate-500">La primera fila debe tener los títulos de las columnas</span>
                            <input type="file" accept=".xlsx,.csv,.txt" onChange={elegir} className="sr-only" />
                        </label>
                        <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-semibold text-slate-500">
                            <span>Obligatorios: nombre, apellidos, DNI/NIE, fecha de nacimiento, teléfono y dirección.</span>
                            <button type="button" onClick={descargarPlantilla} className="font-black text-clinica-primary hover:underline">Descargar plantilla</button>
                        </div>
                    </div>
                )}

                {paso === 'columnas' && (
                    <div className="space-y-5">
                        <p className="text-sm font-semibold text-slate-600">
                            <strong className="text-slate-900">{archivo?.name}</strong>: {filas.length} filas. Revisa qué columna va a cada campo; las reconocidas ya están puestas.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                            {CAMPOS.map(c => (
                                <div key={c.id} className="flex items-center gap-3">
                                    <label htmlFor={`col-${c.id}`} className="w-40 shrink-0 text-sm font-bold text-slate-700">
                                        {c.texto}{c.obligatorio && <span className="text-rose-600"> *</span>}
                                    </label>
                                    <select id={`col-${c.id}`} value={mapa[c.id]} onChange={e => setMapa(m => ({ ...m, [c.id]: e.target.value }))} className={estiloSelect}>
                                        <option value="">— No importar —</option>
                                        {cabecera.map((t, i) => <option key={i} value={String(i)}>{t || `Columna ${i + 1}`}</option>)}
                                    </select>
                                </div>
                            ))}
                        </div>

                        <div>
                            <h3 className="mb-2 text-xs font-black uppercase tracking-widest text-slate-400">Así se leen las primeras filas</h3>
                            <div className="overflow-x-auto border border-slate-100 rounded-xl">
                                <table className="w-full text-xs">
                                    <thead className="bg-slate-50 text-slate-500 font-black">
                                        <tr>{CAMPOS.filter(c => mapa[c.id] !== '').map(c => <th key={c.id} className="p-2 text-left whitespace-nowrap">{c.texto}</th>)}</tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {filasComoFicha.slice(0, 3).map((f, i) => (
                                            <tr key={i}>{CAMPOS.filter(c => mapa[c.id] !== '').map(c => <td key={c.id} className="p-2 font-semibold text-slate-700 whitespace-nowrap">{f[c.id]}</td>)}</tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {faltan.length > 0 && (
                            <p className="text-xs font-bold text-amber-700">Falta elegir columna para: {faltan.map(c => c.texto).join(', ')}.</p>
                        )}
                        <div className="flex justify-between gap-3">
                            <button type="button" onClick={() => setPaso('archivo')} disabled={trabajando} className="px-5 py-2.5 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-100">Otro archivo</button>
                            <button type="button" onClick={() => enviar(true)} disabled={trabajando || faltan.length > 0}
                                    className="px-5 py-2.5 rounded-xl text-sm font-black text-white bg-clinica-primary hover:bg-blue-700 disabled:opacity-50">
                                {trabajando ? `Comprobando ${progreso.hechas} de ${progreso.total}…` : 'Comprobar filas'}
                            </button>
                        </div>
                    </div>
                )}

                {(paso === 'informe' || paso === 'hecho') && informe && (
                    <div className="space-y-5">
                        <div className="grid grid-cols-3 gap-3">
                            {[
                                [paso === 'hecho' ? 'Importados' : 'Listos para importar', paso === 'hecho' ? informe.creadas : informe.validas, 'text-emerald-700'],
                                ['Ya estaban', informe.existentes.length, 'text-slate-600'],
                                ['Con errores', informe.errores.length, informe.errores.length ? 'text-rose-700' : 'text-slate-400']
                            ].map(([t, n, c]) => (
                                <div key={t} className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
                                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">{t}</div>
                                    <div className={`text-2xl font-black mt-1 ${c}`}>{n}</div>
                                </div>
                            ))}
                        </div>

                        {informe.existentes.length > 0 && (
                            <p className="text-xs font-semibold text-slate-500">
                                Los que ya estaban (mismo DNI/NIE) no se tocan. Por ejemplo: {informe.existentes.slice(0, 3).map(e => e.nombre).join(', ')}{informe.existentes.length > 3 ? '…' : ''}
                            </p>
                        )}

                        {informe.errores.length > 0 && (
                            <div>
                                <h3 className="mb-2 text-xs font-black uppercase tracking-widest text-slate-400">Filas que no se importan (corrígelas en el archivo y vuelve a importarlo)</h3>
                                <ul className="max-h-60 overflow-y-auto divide-y divide-slate-100 border border-slate-100 rounded-xl text-sm">
                                    {informe.errores.slice(0, 200).map(e => (
                                        <li key={e.fila} className="p-3">
                                            <span className="font-black text-slate-900">Fila {e.fila}</span>
                                            {e.nombre && <span className="font-semibold text-slate-500"> · {e.nombre}</span>}
                                            <span className="block text-xs font-semibold text-rose-700 mt-0.5">{e.mensajes.join(' · ')}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {paso === 'informe' ? (
                            <div className="flex justify-between gap-3">
                                <button type="button" onClick={() => setPaso('columnas')} disabled={trabajando} className="px-5 py-2.5 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-100">Volver a las columnas</button>
                                <button type="button" onClick={() => enviar(false)} disabled={trabajando || informe.validas === 0}
                                        className="px-5 py-2.5 rounded-xl text-sm font-black text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50">
                                    {trabajando ? `Importando ${progreso.hechas} de ${progreso.total}…` : `Importar ${informe.validas} pacientes`}
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <p className="text-sm font-semibold text-slate-600">
                                    Listo. El consentimiento informado de los pacientes importados se registra en su primera visita.
                                </p>
                                <div className="flex justify-end">
                                    <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-xl text-sm font-black text-white bg-clinica-primary hover:bg-blue-700">Cerrar</button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
}
